import Modal from "react-responsive-modal";
import {
  useAuth,
  useFlagReview,
  useI18N,
  useZoomDetection,
  useModalHeight,
} from "common/hooks";
import { Textarea } from "@/shadcn/components/ui/textarea";
import { useEffect, useRef, useState } from "react";
import { Input } from "@/shadcn/components/ui/input";
import { XIcon } from "lucide-react";
import { readFile } from "@/helpers/cropImage";
import { AddPhotoImageData, VenueDetails, VenueReview } from "common/types";
import Zoom from "react-medium-image-zoom";

import { trackEvent } from "common/analytics";
import { useCustomToast } from "@/hooks/useCustomToast";
import { useDarkMode } from "@/hooks/useDarkMode";
import WideButton from "@/components/common/WideButton";
import Loader from "@/components/common/Loader";
import { Storage } from "common/storage";
import { cn } from "@/lib/utils";
import { StandardButton } from "@/components/common/StandardButton";

// A review's internal `accessibility_experience` is "cool" / "weird"; the canonical analytics
// `experience_type` value is "sweet" / "sour" (cool = sweet, weird = sour — mirrors the UI label).
// Returns "" when there's no experience so we never report a spurious "sour".
const experienceTypeForAnalytics = (accessibilityExperience?: string | null): string =>
  !accessibilityExperience ? "" : accessibilityExperience === "cool" ? "sweet" : "sour";

type FlagVenueReviewProps = {
  review: VenueReview;
  venueName: string;
  isOpen: boolean;
  // isSuccess distinguishes a post-submit close (skip flag_experience_form_closed —
  // experience_flagged already fired) from a user dismiss (X/backdrop/Esc → fire _closed).
  onClose: (isSuccess?: boolean) => void;
  venueDetails: VenueDetails;
};

function FlagVenueReview({
  review,
  venueName,
  isOpen,
  onClose,
  venueDetails,
}: FlagVenueReviewProps) {
  const i18n = useI18N();
  const { modalStyle, modalClassName } = useModalHeight();
  const { user } = useAuth();

  const showToast = useCustomToast();

  const [note, setNote] = useState("");
  const noteRef = useRef<HTMLTextAreaElement>(null);
  const [attachedImages, setAttachedImages] = useState<AddPhotoImageData[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>(null);
  const isZoomedIn = useZoomDetection();
  const isDark = useDarkMode();
  const { mutate, error, isPending, data } = useFlagReview();

  useEffect(() => {
    if (error) {
      console.log("useFlagPhoto", error);

      showToast(error.message, "error");
    }
  }, [error]);

  useEffect(() => {
    if (data) {
      console.log("useFlagPhoto", data);
      trackEvent("experience_flagged", {
        brand_id: venueDetails?.brandId ?? "",
        brand_name: venueDetails?.brandName ?? "",
        business_id: venueDetails.id.toString(),
        experience_type: experienceTypeForAnalytics(review?.accessibility_experience),
        photos_count: attachedImages.length,
        place_name: venueName,
      });
      showToast(data.message, "success");
      setAttachedImages([]);
      setNote("");
      onClose(true);
    }
  }, [data]);

  const onFileChange = async (e) => {
    const newImages = Array.from(e.target.files).map(async (file) => {
      const imageDataUrl = await readFile(file);
      return {
        imageUrl: imageDataUrl,
        note: "",
        selectedOption: undefined,
        key: undefined,
      };
    });
    const imgs = await Promise.all(newImages);
    setAttachedImages((prev) => [...prev, ...imgs]);
  };

  const submit = () => {
    if (note.length < 5) {
      noteRef.current?.focus();
      setErrorMessage("Note must be at least 5 characters long");
      return;
    }
    setErrorMessage(null);

    mutate({
      reviewId: review.id.toString(),
      accessToken: Storage.getCurrentUserToken(),
      notes: note,
      images: attachedImages,
    });
  };

  const firstFocusRef = useRef(null); // Reference for the element we need to set focus

  // Focus on the element when the component mounts
  useEffect(() => {
    if (firstFocusRef.current) {
      firstFocusRef.current.focus();
    }
  }, []);

  return (
    <Modal
      focusTrapped
      center
      blockScroll={true}
      animationDuration={0}
      open={isOpen}
      onClose={() => onClose()}
      classNames={{
        modal: "bg-[transparent] p-0 focus:outline-0 shadow-none md:w-[480px]",
        closeButton: "hidden",
      }}
    >
      {isPending && <Loader />}
      <section
        style={modalStyle}
        className={cn(modalClassName)}
        role="none"
        aria-labelledby="modal-title"
      >
        <header
          className={`py-4 min-h-14 flex items-center justify-between text-center px-6 border-b border-border-token-brand-minimal bg-bg-brand-secondary-default ${!isZoomedIn ? "sticky top-0 z-10" : "relative z-10"} relative`}
        >
          <StandardButton
            onClick={() => {
              setNote("");
              onClose();
            }}
            variant="closeModal"
            icon={<XIcon color={isDark ? "#A39FE8" : "#352FC3"} />}
            aria-label="Close modal"
            tabIndex={0}
            className="flex-shrink-0"
          />
          <h1
            className="title-s-semibold text-center text-fg-neutral-strong flex-1 px-4"
            ref={firstFocusRef}
            tabIndex={0}
            id="modal-title"
          >
            {i18n.get("flagReview")}
          </h1>
          <div className="flex-shrink-0 w-10"></div>
          {" "}
          {/* Spacer to balance the layout */}
        </header>

        <div aria-label="Modal Content" className="flex-grow overflow-y-auto">
          <div className="self-stretch px-6 pt-2 flex-col justify-start items-center gap-2 flex">
            <div className="self-stretch h-30 pt-2 opacity-80 flex-col justify-start items-start gap-1 flex">
              <div className="self-stretch h-12 flex-col justify-start items-start gap-2 flex">
                <div className="self-stretch !text-fg-brand-subtle label-s-medium">
                  {venueName}
                </div>
                <h1 className="self-stretch text-fg-neutral-strong title-s-semibold">
                  {i18n.get("helpUsCatchEveryError")}
                </h1>
              </div>
            </div>

            <div className="w-full flex-col justify-center items-start gap-1 flex pt-1">
              <div className=" w-full">
                <Textarea
                  id="review"
                  placeholder={i18n.get("tellUsWhatIsIncorrect")}
                  className="scrollbar-thin h-28"
                  value={note}
                  onChange={(e) => {
                    setErrorMessage(null);
                    setNote(e.target.value);
                  }}
                  maxLength={500}
                  ref={noteRef}
                  error={(note.length > 0 && note.length < 5) || Boolean(errorMessage)}
                />
                <div
                  className={`label-s-medium mt-3 ${
                    note.length > 0 && note.length < 5
                      ? "text-fg-error-subtle"
                      : "text-fg-neutral-subtle"
                  }`}
                >
                  {note.length > 0 && note.length < 5
                    ? i18n.get("pleaseEnterAtLeast5Characters")
                    : `${note.length}/500 characters`}
                </div>
                {errorMessage && !(note.length > 0 && note.length < 5) && (
                  <div className="text-fg-error-subtle">{errorMessage}</div>
                )}
              </div>
            </div>

            <div className="w-full flex-col justify-center items-start gap-1 flex">
              <div className="grid gap-4 w-full">
                {attachedImages.map((image, index) => (
                  <div key={index} className="grid gap-2">
                    <div className="relative w-full h-auto">
                      <Zoom>
                        <img
                          src={image.imageUrl}
                          alt="attached image"
                          className="w-full h-[300px] object-cover rounded-[10px]"
                        />
                      </Zoom>
                      <StandardButton
                        onClick={() => {
                          setAttachedImages(
                            attachedImages.filter(
                              (_, imgIndex) => imgIndex !== index,
                            ),
                          );
                        }}
                        variant="removeImage"
                        position="absolute"
                        className="top-2 right-3 m-2"
                        imageSrc="/images/Close.svg"
                        imageAlt="Remove image"
                        imageContainer={true}
                        imageContainerClassName="w-6 h-6 p-1 bg-[#f9f8fd] rounded border border-[#cbc9f2] justify-center items-center inline-flex"
                        aria-label={`Remove attached image ${index + 1}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <footer
          className={`py-3 min-h-14 flex items-center justify-center text-center px-6 bg-bg-brand-secondary-default z-1000 ${!isZoomedIn ? "sticky bottom-0" : "relative"}`}
        >
          <div className="flex-col md:flex-row justify-end items-center gap-4 flex w-full">
            <div className="w-full justify-start items-center gap-4 inline-flex">
              <label
                htmlFor="picture" // Wrap the entire div with the label
                className="grow shrink basis-0 h-12 px-6 py-3 bg-container-brand-minimal rounded-xl border border-[#cbc9f2] justify-center items-center gap-3 flex cursor-pointer focus-blue"
                aria-label="Add photos"
                tabIndex={0}
                role="button"
              >
                <div className="text-center text-fg-neutral-strong label-l-bold w-full">
                  {i18n.get("addPhotos")}
                </div>
                <Input
                  id="picture"
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={onFileChange}
                />
              </label>
            </div>
            <div className="w-full justify-center items-center inline-flex">
              <WideButton
                className="grow"
                disabled={note.length < 5}
                onClick={() => {
                  submit();
                }}
              >
                {i18n.get("submitFlag")}
              </WideButton>
            </div>
          </div>
        </footer>
      </section>
    </Modal>
  );
}

export default FlagVenueReview;
